/**
 * Always-available feedback entry point: a vertical tab pinned to the right edge
 * that opens a small panel of feedback choices.
 *
 * It replaces the Feedback tab on the customer-facing pages, so it has to stay
 * reachable at any scroll position. The page container reserves a right gutter
 * (see UserTestingPage) so the tab never sits on top of the carousel arrows.
 *
 * The panel also offers the tour again. The tour itself only ever opens once per
 * shopper, and the tab is the one control that is on screen at every scroll
 * position, so it is the only place a second viewing can be asked for.
 */

import { Compass, MessageSquare, TriangleAlert, X } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Lets the onboarding tour spotlight the tab as its closing step. */
export const FEEDBACK_TAB_TOUR_ATTR = 'data-tour-feedback-tab';

export type FeedbackChoice = 'rate' | 'report';

/** Replaying the tour is not feedback, so it is not a FeedbackChoice. */
type PanelAction = FeedbackChoice | 'tour';

const ACTIONS: Array<{
  action: PanelAction;
  icon: typeof MessageSquare;
  titleKey: string;
  bodyKey: string;
}> = [
  {
    action: 'rate',
    icon: MessageSquare,
    titleKey: 'feedbackPanel.rateTitle',
    bodyKey: 'feedbackPanel.rateBody',
  },
  {
    action: 'report',
    icon: TriangleAlert,
    titleKey: 'feedbackPanel.reportTitle',
    bodyKey: 'feedbackPanel.reportBody',
  },
  {
    action: 'tour',
    icon: Compass,
    titleKey: 'feedbackPanel.tourTitle',
    bodyKey: 'feedbackPanel.tourBody',
  },
];

interface FeedbackSideTabProps {
  onSelect: (choice: FeedbackChoice) => void;
  /** Omit when there is no row worth spotlighting, which hides the tour row. */
  onReplayTour?: () => void;
  /** Overrides an action's subtitle key, for pages whose dialog asks different questions. */
  bodyKeys?: Partial<Record<FeedbackChoice, string>>;
}

const FeedbackSideTab: React.FC<FeedbackSideTabProps> = ({ onSelect, onReplayTour, bodyKeys }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const tabRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const close = useCallback((returnFocus: boolean) => {
    setIsOpen(false);
    if (returnFocus) tabRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || tabRef.current?.contains(target)) return;
      close(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close(true);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, close]);

  useEffect(() => {
    if (!isOpen) return;
    panelRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
  }, [isOpen]);

  const handleAction = (action: PanelAction) => {
    close(false);
    if (action === 'tour') {
      onReplayTour?.();
      return;
    }
    onSelect(action);
  };

  const actions = ACTIONS.filter(({ action }) => action !== 'tour' || onReplayTour);

  return (
    <>
      <button
        ref={tabRef}
        type="button"
        {...{ [FEEDBACK_TAB_TOUR_ATTR]: 'true' }}
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        className="fixed right-0 top-1/2 z-30 -translate-y-1/2 bg-indigo-brand-black px-2 py-4 text-sm font-medium text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-brand-black"
        // Vertical label stays real text, so it is still read and translated.
        style={{ writingMode: 'vertical-rl' }}
      >
        {t('feedbackPanel.tab')}
      </button>

      {isOpen && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label={t('feedbackPanel.title')}
          className="fixed right-10 top-1/2 z-40 w-[min(20rem,calc(100vw-4rem))] -translate-y-1/2 border border-indigo-brand-border bg-white shadow-lg"
        >
          <div className="flex items-center justify-between bg-indigo-brand-black px-4 py-3">
            <h2 className="text-sm font-semibold text-white">{t('feedbackPanel.title')}</h2>
            <button
              type="button"
              onClick={() => close(true)}
              aria-label={t('common.close')}
              className="text-white/80 hover:text-white"
            >
              <X size={16} />
            </button>
          </div>

          {actions.map(({ action, icon: Icon, titleKey, bodyKey }) => (
            <button
              key={action}
              type="button"
              onClick={() => handleAction(action)}
              className="flex w-full items-start gap-3 border-b border-indigo-brand-border px-4 py-4 text-left last:border-b-0 hover:bg-indigo-brand-off-white"
            >
              <Icon size={22} className="mt-0.5 flex-shrink-0 text-indigo-brand-text" />
              <span>
                <span className="block text-sm font-semibold text-indigo-brand-text">
                  {t(titleKey)}
                </span>
                <span className="block text-sm text-indigo-brand-text/70">
                  {t((action !== 'tour' && bodyKeys?.[action]) || bodyKey)}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </>
  );
};

export default FeedbackSideTab;
